/**
 * Anmeldung mit E-Mail/Passwort und — wenn fuer den Account aktiv — TOTP als
 * zweitem Schritt. Better-Auth signalisiert den zweiten Faktor ueber
 * `twoFactorRedirect`; erst nach `verifyTotp` existiert eine Session.
 */
import { loginSchema, totpCodeSchema } from '@tickethub/shared';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { authClient } from '@/lib/auth-client';
import { useSession } from '@/lib/session';

type Step = 'credentials' | 'totp' | 'forgot';

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { reload } = useSession();

  const [step, setStep] = useState<Step>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  /**
   * Passwort-Reset anfordern. Die Antwort ist **immer** gleich — sie darf nicht
   * verraten, ob es die Adresse gibt (security.md). Deshalb auch keine
   * Fehlerbehandlung, die zwischen den Faellen unterscheidet.
   */
  async function requestReset(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    setPending(true);
    try {
      await authClient.requestPasswordReset({
        email,
        redirectTo: `${window.location.origin}/reset-password`,
      });
      setResetSent(true);
    } finally {
      setPending(false);
    }
  }

  async function finishLogin() {
    await reload();
    void navigate('/', { replace: true });
  }

  async function submitCredentials(event: FormEvent) {
    event.preventDefault();
    setFormError(null);

    const parsed = loginSchema.safeParse({ email, password });
    if (!parsed.success) {
      setFormError(parsed.error.issues[0]?.message ?? t('login.failed'));
      return;
    }

    setPending(true);
    try {
      const result = await authClient.signIn.email(parsed.data);
      if (result.error) {
        setFormError(result.error.status === 429 ? t('login.rate_limited') : t('login.failed'));
        return;
      }
      // Bei aktivem 2FA liefert Better-Auth `twoFactorRedirect: true` statt einer
      // Session. Der Client-Typ kennt das Feld nicht — es stammt aus dem
      // Server-Plugin —, deshalb hier eine enge Typzusicherung.
      const signInData = result.data as { twoFactorRedirect?: boolean } | null;
      if (signInData?.twoFactorRedirect === true) {
        setStep('totp');
        return;
      }
      await finishLogin();
    } finally {
      setPending(false);
    }
  }

  async function submitTotp(event: FormEvent) {
    event.preventDefault();
    setFormError(null);

    const parsed = totpCodeSchema.safeParse({ code, trust_device: trustDevice });
    if (!parsed.success) {
      setFormError(parsed.error.issues[0]?.message ?? t('login.totp_failed'));
      return;
    }

    setPending(true);
    try {
      const result = await authClient.twoFactor.verifyTotp({
        code: parsed.data.code,
        trustDevice: parsed.data.trust_device,
      });
      if (result.error) {
        setFormError(t('login.totp_failed'));
        return;
      }
      await finishLogin();
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-muted/30 p-6">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>
            {step === 'forgot'
              ? t('login.forgot_title')
              : step === 'credentials'
                ? t('login.title')
                : t('login.totp_title')}
          </CardTitle>
          <CardDescription>
            {step === 'forgot'
              ? t('login.forgot_hint')
              : step === 'credentials'
                ? t('login.subtitle')
                : t('login.totp_hint')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {step === 'forgot' ? (
            resetSent ? (
              <div className="space-y-4">
                <p className="text-sm">{t('login.forgot_sent')}</p>
                <Button
                  className="w-full"
                  variant="outline"
                  onClick={() => {
                    setResetSent(false);
                    setStep('credentials');
                  }}
                >
                  {t('login.forgot_back')}
                </Button>
              </div>
            ) : (
              <form className="space-y-4" onSubmit={(event) => void requestReset(event)}>
                <div className="space-y-2">
                  <Label htmlFor="reset-email">{t('login.email')}</Label>
                  <Input
                    id="reset-email"
                    type="email"
                    autoComplete="username"
                    required
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                </div>
                <Button type="submit" className="w-full" disabled={pending}>
                  {pending ? t('common.loading') : t('login.forgot_submit')}
                </Button>
                <Button
                  type="button"
                  className="w-full"
                  variant="ghost"
                  onClick={() => setStep('credentials')}
                >
                  {t('login.forgot_back')}
                </Button>
              </form>
            )
          ) : step === 'credentials' ? (
            <form className="space-y-4" onSubmit={(event) => void submitCredentials(event)}>
              <div className="space-y-2">
                <Label htmlFor="email">{t('login.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">{t('login.password')}</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              </div>
              {formError ? <p className="text-sm text-destructive">{formError}</p> : null}
              <Button type="submit" className="w-full" disabled={pending}>
                {pending ? t('common.loading') : t('login.submit')}
              </Button>
              <Button
                type="button"
                className="w-full"
                variant="ghost"
                onClick={() => {
                  setFormError(null);
                  setStep('forgot');
                }}
              >
                {t('login.forgot_link')}
              </Button>
            </form>
          ) : (
            <form className="space-y-4" onSubmit={(event) => void submitTotp(event)}>
              <div className="space-y-2">
                <Label htmlFor="totp">{t('login.totp_label')}</Label>
                <Input
                  id="totp"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  required
                  value={code}
                  onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
                />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={trustDevice}
                  onChange={(event) => setTrustDevice(event.target.checked)}
                />
                {t('login.totp_trust_device')}
              </label>
              {formError ? <p className="text-sm text-destructive">{formError}</p> : null}
              <Button type="submit" className="w-full" disabled={pending}>
                {pending ? t('common.loading') : t('login.totp_submit')}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
      <LanguageSwitcher />
    </div>
  );
}
